export class PercussionPatternStrategy {
  generate(ctx, chord, oct, baseDegree, rand, bpb, genre) {
    if (genre === 'breakcore') return this._genBreakcorePerc(ctx, oct, rand, bpb);
    if (genre === 'jazz') return this._genJazzPerc(ctx, oct, rand, bpb);
    if (genre === 'ambient') return this._genAmbientPerc(ctx, oct, rand, bpb);
    if (genre === 'lofi') return this._genLofiPerc(ctx, oct, rand, bpb);
    if (genre === 'world') return this._genWorldPerc(ctx, oct, rand, bpb);
    if (genre === 'electronic') return this._genElectronicPerc(ctx, oct, rand, bpb);
    return this._genPercussion(ctx, oct, rand, bpb);
  }

  _genPercussion(ctx, oct, rand, bpb = 4) {
    const pattern = [];
    const percOct = oct - 1;
    const kickNote = ctx.noteFromDegree(0, percOct);
    const snareNote = ctx.noteFromDegree(2, percOct + 1);
    const hatNote = ctx.noteFromDegree(4, percOct + 2);
    const hatSubdivision = rand() < 0.5 ? 0.5 : 0.25;
    const hatChance = ctx.percussionStyle === 'sparse' ? 0.35 : ctx.percussionStyle === 'syncopated' ? 0.55 : 0.72;

    if (ctx.percussionStyle === 'four-on-the-floor') {
      for (let b = 0; b < bpb; b++) pattern.push({ beat: b, note: kickNote, duration: 0.08 });
      for (let b = 1; b < bpb; b += 2) pattern.push({ beat: b, note: snareNote, duration: 0.06 });
    } else if (ctx.percussionStyle === 'syncopated' || ctx.percussionStyle === 'offbeat') {
      [0, Math.floor(bpb * 0.375), bpb - 1].forEach(b => { if (b < bpb) pattern.push({ beat: b, note: kickNote, duration: 0.08 }); });
      [1, Math.floor(bpb * 0.625), bpb - 1].forEach(b => { if (b < bpb && rand() < 0.9) pattern.push({ beat: b, note: snareNote, duration: 0.06 }); });
    } else {
      pattern.push({ beat: 0, note: kickNote, duration: 0.08 });
      if (rand() < 0.7 && bpb >= 3) pattern.push({ beat: Math.floor(bpb / 2), note: kickNote, duration: 0.08 });
      if (rand() < 0.35 && bpb >= 4) pattern.push({ beat: bpb - 0.5, note: kickNote, duration: 0.06 });
      if (rand() < 0.4 && bpb >= 3) pattern.push({ beat: Math.floor(bpb / 2), note: snareNote, duration: 0.06 });
    }

    for (let b = 0; b < bpb; b += hatSubdivision) {
      if (ctx.percussionStyle === 'sparse' || ctx.percussionStyle === 'pulse' || ctx.percussionStyle === 'timpani') {
        if (rand() < 0.28) pattern.push({ beat: b, note: hatNote, duration: 0.03 });
      } else if (rand() < hatChance) {
        pattern.push({ beat: b, note: hatNote, duration: 0.03 });
      }
    }
    return pattern;
  }

  _genBreakcorePerc(ctx, oct, rand, bpb = 4) {
    const pattern = [];
    const percOct = oct - 1;
    const kickNote = ctx.noteFromDegree(0, percOct);
    const snareNote = ctx.noteFromDegree(2, percOct + 1);
    const hatNote = ctx.noteFromDegree(4, percOct + 2);
    const crashNote = ctx.noteFromDegree(6, percOct + 2);

    const style = ctx.percussionStyle;
    if (style === 'breakcore-amen') {
      const amenKicks = [0, 0.75, 1.5, 2.5, 3.25];
      const amenSnares = [0.5, 1, 1.25, 2, 2.75, 3, 3.5, 3.75];
      for (const b of amenKicks) if (b < bpb) pattern.push({ beat: b, note: kickNote, duration: 0.04 });
      for (const b of amenSnares) if (b < bpb && rand() < 0.85) pattern.push({ beat: b, note: snareNote, duration: 0.04 });
      for (let b = 0; b < bpb; b += 0.25) if (rand() < 0.25) pattern.push({ beat: b, note: snareNote, duration: 0.02 });
    } else if (style === 'breakcore-chop') {
      for (let b = 0; b < bpb; b += 0.25) {
        const r = rand();
        if (r < 0.3) pattern.push({ beat: b, note: kickNote, duration: 0.03 + rand() * 0.05 });
        if (r > 0.5 && r < 0.75) pattern.push({ beat: b, note: snareNote, duration: 0.02 + rand() * 0.04 });
        if (r > 0.85) pattern.push({ beat: b, note: hatNote, duration: 0.02 });
      }
      if (rand() < 0.5) {
        const rollStart = Math.floor(rand() * bpb);
        for (let r = 0; r < 4; r++) {
          const b = rollStart + r * 0.125;
          if (b < bpb) pattern.push({ beat: b, note: snareNote, duration: 0.02 });
        }
      }
    } else {
      for (let b = 0; b < bpb; b += 0.25) {
        if (rand() < 0.45) pattern.push({ beat: b, note: kickNote, duration: 0.03 });
        if (rand() < 0.55) pattern.push({ beat: b, note: snareNote, duration: 0.02 });
      }
    }
    for (let b = 0; b < bpb; b += 0.25) if (rand() < 0.6) pattern.push({ beat: b, note: hatNote, duration: 0.015 + rand() * 0.02 });
    if (rand() < 0.3) pattern.push({ beat: 0, note: crashNote, duration: 0.1 });
    return pattern;
  }

  _genJazzPerc(ctx, oct, rand, bpb = 4) {
    const pattern = [];
    const percOct = oct - 1;
    const kickNote = ctx.noteFromDegree(0, percOct);
    const snareNote = ctx.noteFromDegree(2, percOct + 1);
    const rideNote = ctx.noteFromDegree(4, percOct + 2);
    if (ctx.percussionStyle === 'swing-ride') {
      for (let beat = 0; beat < bpb; beat++) {
        pattern.push({ beat, note: rideNote, duration: 0.06 });
        if (rand() < 0.7) pattern.push({ beat: beat + 0.67, note: rideNote, duration: 0.04 });
      }
      if (rand() < 0.4) pattern.push({ beat: 0, note: kickNote, duration: 0.06 });
      if (rand() < 0.3 && bpb >= 3) pattern.push({ beat: Math.floor(bpb / 2), note: kickNote, duration: 0.05 });
      if (rand() < 0.5) pattern.push({ beat: 1 + rand(), note: snareNote, duration: 0.04 });
      if (rand() < 0.4 && bpb >= 4) pattern.push({ beat: 3 + rand() * 0.5, note: snareNote, duration: 0.04 });
    } else if (ctx.percussionStyle === 'jazz-brush') {
      for (let beat = 0; beat < bpb; beat++) {
        if (rand() < 0.6) pattern.push({ beat, note: snareNote, duration: 0.08 });
        if (rand() < 0.4) pattern.push({ beat: beat + 0.5, note: snareNote, duration: 0.05 });
      }
      pattern.push({ beat: 0, note: kickNote, duration: 0.05 });
    } else {
      for (const b of [0, 1.5]) if (b < bpb) pattern.push({ beat: b, note: kickNote, duration: 0.06 });
      for (const b of [0.5, 1, 1.5, 2, 2.5, 3, 3.5]) if (b < bpb && rand() < 0.7) pattern.push({ beat: b, note: snareNote, duration: 0.03 });
      for (let b = 0; b < bpb; b += 0.5) if (rand() < 0.5) pattern.push({ beat: b, note: rideNote, duration: 0.04 });
    }
    return pattern;
  }

  _genAmbientPerc(ctx, oct, rand, bpb = 4) {
    const pattern = [];
    if (rand() < 0.4) return pattern;
    const softNote = ctx.noteFromDegree(4, oct + 1);
    const numHits = 1 + Math.floor(rand() * 2);
    for (let i = 0; i < numHits; i++) pattern.push({ beat: rand() * bpb, note: softNote, duration: 0.03 });
    return pattern;
  }

  _genLofiPerc(ctx, oct, rand, bpb = 4) {
    const pattern = [];
    const percOct = oct - 1;
    const kickNote = ctx.noteFromDegree(0, percOct);
    const snareNote = ctx.noteFromDegree(2, percOct + 1);
    const hatNote = ctx.noteFromDegree(4, percOct + 2);
    pattern.push({ beat: 0, note: kickNote, duration: 0.1 });
    if (bpb >= 3) {
      const mid = Math.floor(bpb / 2);
      pattern.push({ beat: mid + (rand() < 0.3 ? 0.5 : 0), note: kickNote, duration: 0.08 });
    }
    if (bpb >= 2) pattern.push({ beat: 1 + rand() * 0.05, note: snareNote, duration: 0.06 });
    if (bpb >= 4) pattern.push({ beat: 3 + rand() * 0.05, note: snareNote, duration: 0.06 });
    for (let b = 0; b < bpb; b += 0.5) if (rand() < 0.5) pattern.push({ beat: b + rand() * 0.04, note: hatNote, duration: 0.03 });
    return pattern;
  }

  _genWorldPerc(ctx, oct, rand, bpb = 4) {
    const pattern = [];
    const percOct = oct - 1;
    const lowNote = ctx.noteFromDegree(0, percOct);
    const midNote = ctx.noteFromDegree(2, percOct + 1);
    const highNote = ctx.noteFromDegree(4, percOct + 2);
    const bellNote = ctx.noteFromDegree(6, percOct + 2);

    if (ctx.percussionStyle === 'clave') {
      const claveBeats = bpb >= 4 ? [0, 1.5, 2.5, 3, 3.5] : [0, 1, 1.75, 2.5];
      for (const b of claveBeats) if (b < bpb) pattern.push({ beat: b, note: midNote, duration: 0.04 });
      for (let b = 0; b < bpb; b += 0.5) if (rand() < 0.4) pattern.push({ beat: b, note: lowNote, duration: 0.06 });
    } else if (ctx.percussionStyle === 'tabla') {
      for (let b = 0; b < bpb; b += 0.5) {
        const r = rand();
        if (r < 0.35) pattern.push({ beat: b, note: lowNote, duration: 0.08 });
        else if (r < 0.6) pattern.push({ beat: b, note: highNote, duration: 0.04 });
      }
      for (let b = 0; b < bpb; b++) if (rand() < 0.5) pattern.push({ beat: b, note: bellNote, duration: 0.03 });
    } else {
      const poly = bpb / 3;
      for (let b = 0; b < bpb; b += poly) pattern.push({ beat: b, note: midNote, duration: 0.05 });
      for (let b = 0; b < bpb; b++) {
        pattern.push({ beat: b, note: lowNote, duration: 0.07 });
        if (rand() < 0.4) pattern.push({ beat: b + 0.5, note: highNote, duration: 0.03 });
      }
    }
    return pattern;
  }

  _genElectronicPerc(ctx, oct, rand, bpb = 4) {
    const pattern = [];
    const percOct = oct - 1;
    const kickNote = ctx.noteFromDegree(0, percOct);
    const snareNote = ctx.noteFromDegree(2, percOct + 1);
    const hatNote = ctx.noteFromDegree(4, percOct + 2);
    const clapNote = ctx.noteFromDegree(3, percOct + 1);
    for (let b = 0; b < bpb; b++) pattern.push({ beat: b, note: kickNote, duration: 0.06 });
    for (let b = 1; b < bpb; b += 2) pattern.push({ beat: b, note: clapNote, duration: 0.05 });
    for (let b = 0; b < bpb; b += 0.5) {
      const isOpen = (Math.floor(b * 2) % 2 === 1);
      pattern.push({ beat: b, note: hatNote, duration: isOpen ? 0.08 : 0.03 });
    }
    if (rand() < 0.4) {
      for (let b = 0.75; b < bpb; b += 1) {
        if (rand() < 0.3) pattern.push({ beat: b, note: snareNote, duration: 0.02 });
      }
    }
    return pattern;
  }
}
